define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/fx",
    "dojo/_base/lang",
    "dojo/_base/window",
    "dojo/aspect",
    "dojo/on",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/mouse",
    "dojo/touch",
    "./LayoutChildProperty",
    "./LayoutContainerEvent",
    "./LayoutUtils",
    "./PanelContainer",
    "./TitleBar",
    "./TitledPane"
], function (declare, event, fx, lang, window, aspect, on, domClass, domConstruct, domGeometry, domStyle,
             mouse, touch, LayoutChildProperty, LayoutContainerEvent, LayoutUtils, PanelContainer, TitleBar, TitledPane) {

    /**
     * Defines an object that manages the transfer of a panel from one PanelContainer to another via drag.
     * It handles transfer between existing PanelContainers but can be sub-classed to perform container creation
     * and destruction.
     */
    return declare(null, {

        /*
         * _containers         an array containing the containers to which the dragger has been attached
         * _moveConnection
         * _releaseConnection
         * _originInfo         this is provided by the panel container upon drag out.  As a minimum it must contain:
         *    region
         *    container
         *    containerSize
         *    widget
         *    properties
         *    mousePoint
         *    box
         * _initialMousePoint
         * _initialBox
         * _currentBox
         * _titledPane
         */

        constructor: function () {
            this._containers = [];
        },

        /**
         * Attaches the bridge to the given PanelContainer so that it will be informed when a widget is dragged out
         * of the container.
         */
        attach: function (panelContainer) {
            this._containers.push(panelContainer);
            var that = this;
            panelContainer.on(LayoutContainerEvent.DRAG_OUT, lang.hitch(this, "_handleDragOut", panelContainer));
            panelContainer.panelDraggerConnections = [
                aspect.after(panelContainer, 'destroy', function () {
                    that.detach(panelContainer);
                })
            ];
        },

        detach: function (panelContainer) {
            panelContainer.panelDraggerConnections.forEach(function (a) {a.remove();});
            this._containers.splice(this._containers.indexOf(panelContainer), 1);
        },

        _handleDragOut: function (fromContainer, eventInfo) {
            // First determine if the drag out should be permitted.
            if (!this._isDragOutPermitted(eventInfo, fromContainer.region)) {
                return;
            }

                // Save origin information.
            if (!this._originInfo) {
                this._originInfo = eventInfo;
                this._originInfo.container = fromContainer;
                this._originInfo.region = fromContainer.region;
                this._originInfo.wasCollapsed = fromContainer.isContainerCollapsed();
                this._originInfo.freeDimension = fromContainer.getParent().getNonCollapsedFreeDimension(fromContainer.region),
                this._originInfo.containerSize = fromContainer.getDragInBox();
                this._originInfo.focusedElement = document.activeElement;
            }
            
            fromContainer.removePanel(eventInfo.widget, eventInfo.properties);
            
            // Show the panel
            this._titledPane = new TitledPane({id : eventInfo.widget.id + "_TitledPane", contentWidget: eventInfo.widget, contentProperties: eventInfo.properties});
            domConstruct.place(this._titledPane.domNode, window.body());
            this._titledPane.startup();
            eventInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);

            // Set positioning and z-order
            domStyle.set(this._titledPane.domNode, "position", "absolute");
            domClass.add(this._titledPane.domNode, "onTopPane");

            // Compute the initial bounds.  Keep the size of the original box and position such that the title bar
            // is centered under the mouse.
            var mousePoint = eventInfo.mousePoint;
            var box = lang.clone(this._originInfo.box);
            var titleHeight = TitleBar.getPreferredHeight();
            box.t = mousePoint.y - titleHeight / 2;
            box.l = mousePoint.x - box.w / 2;
            box.w += 2;
            box.h += titleHeight + 2;
            this._positionPanel(box);

            // Save information for use during drag
            this._initialMousePoint = mousePoint;
            this._initialBox        = box;

            // Listen for mouse events and move nodes
            this._moveConnection = on(fromContainer.ownerDocument, touch.move, lang.hitch(this, "_continueDrag"));
            // The "release" connection may span multiple dragOuts and dragIns.
            if (!this._releaseConnection) {
                this._releaseConnection = on(fromContainer.ownerDocument, touch.release, lang.hitch(this, "_endDrag"));
            }
            
            this._reselectPanel();
        },
        
        _reselectPanel: function () {
            if (this._originInfo && this._originInfo.widget && this._originInfo.widget.properties) {
                this._originInfo.widget.properties.set("isSelected", true);
            }
        },
        
        _refocusPanel: function () {
            // Be sure to only call _refocusPanel in cases where the panel cannot be rendered off-screen to ensure the page does not jump (g2142816)
            if (this._originInfo && this._originInfo.focusedElement) {
                this._originInfo.focusedElement.focus();
            }
        },

        _hidePanel: function () {
            window.body().removeChild(this._titledPane.domNode);
            this._titledPane.destroy();
            delete this._titledPane;
        },

        _positionPanel: function (box) {
            this._titledPane.resize(box);
            this._currentBox = box;
        },

        _continueDrag: function (e) {
            // See if the panel has been dragged into any of the attached PanelContainers
            var box, i, container, containerBox, permissibleRegions;
            for (i = 0; i < this._containers.length; i += 1) {
                container = this._containers[i];

                // First check if drag into this container is permitted
                if (this._isDragInPermitted(this._originInfo, container.region)) {
                    containerBox = container.getDragInBox();
                    if (containerBox.l < e.pageX && e.pageX < (containerBox.l + containerBox.w) &&
                        containerBox.t < e.pageY && e.pageY < (containerBox.t + containerBox.h)) {

                        // Hide the drag window and add the panel to the container
                        this._stopDragEvents();
                        this._hidePanel();
                        container.dragIn(this._originInfo, {x: e.pageX, y: e.pageY});
                        // g2088927: re-select panel and re-focus the activeElement once it is showing again in the drag Panel
                        this._reselectPanel();
                        this._refocusPanel();
                        this._clearDragInfo();
                        return;
                    }
                }
            }

            // Move the panel
            box = lang.clone(this._initialBox);
            box.l += e.pageX - this._initialMousePoint.x;
            box.t += e.pageY - this._initialMousePoint.y;
            this._positionPanel(box);
        },

        _clearDragInfo: function () {
            delete this._initialMousePoint;
            delete this._initialBox;
        },

        _clearOriginInfo: function () {
            delete this._originInfo;
        },

        _stopDragEvents: function () {
            if (this._moveConnection) {
                this._moveConnection.remove();
                delete this._moveConnection;
            }
        },

        _endDrag: function () {
            // If drag ends while a panel is outside of any container then return it to its original container
            // (and original position within that container)
            if (this._titledPane && this._originInfo) {
                // Stop listening to drag events
                this._stopDragEvents();

                // Animate a move back to the original location
                var that = this;
                this._initialBox = this._currentBox;
                // Animate from initial box to original box
                var animation = new fx.Animation({
                    curve: [0.0, 1.0],
                    onAnimate : function (value) {
                        var box;
                        if (that._initialBox) {
                            box = lang.clone(that._initialBox);
                            box.l += Math.floor(value * (that._originInfo.box.l - box.l));
                            box.t += Math.floor(value * (that._originInfo.box.t - box.t));
                            that._positionPanel(box);
                        }
                    },
                    onEnd: function () {
                        that._hidePanel();
                        that._originInfo.container.dragIn(that._originInfo);
                        // g2088927: re-select panel and re-focus the activeElement once it is showing again in the drag Panel
                        that._reselectPanel();
                        that._refocusPanel();
                        that._clearDragInfo();
                        that._clearOriginInfo();
                    }
                });
                if (LayoutUtils.isAnimationEnabled()) {
                    animation.play();
                } else {
                    animation.onEnd();
                }
            } else {
                this._clearOriginInfo();
            }
            this._releaseConnection.remove();
            delete this._releaseConnection;
        },

        _isDragOutPermitted: function(dragInfo, fromRegion) {
            // Only permit drag out if the panel is allowed to occupy at least one region other than its current one.
            var permissibleRegions = dragInfo.properties.permissibleRegions;
            var i;
            if (permissibleRegions) {
                for (i = 0; i < permissibleRegions.length; i++) {
                    if (permissibleRegions[i] !== fromRegion) {
                        return true;
                    }
                }
                return false;
            }
            return true;
        },

        _isDragInPermitted: function(originInfo, toRegion) {
            // Permit drag into a listed region or back into the original region
            var permissibleRegions = originInfo.properties.permissibleRegions;
            return originInfo.region === toRegion || !permissibleRegions || permissibleRegions.indexOf(toRegion) > 0;
        }
    });
});